import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { AppearanceScreen } from "./AppearanceScreen";
import { BillingBanner } from "./BillingBanner";
import { BoxesScreen } from "./BoxesScreen";
import { createBoxBlocker } from "./boxSale";
import { CatalogScreen } from "./CatalogScreen";
import { HomeActions, HomeScreen } from "./HomeScreen";
import { PurchasesScreen } from "./PurchasesScreen";
import { isPlanApproved } from "./plan";
import { SetupChecklist } from "./SetupChecklist";
import { SetupScreen } from "./SetupScreen";
import { ShipOrdersScreen } from "./ShipOrdersScreen";
import { NAV_SCREENS, useScreen } from "./screens";
import { useTRPC } from "./trpc";
import { usePlacements } from "./usePlacements";

/**
 * The embedded admin: the Setup screen on an install's first run (the shop gets its own Loot OS
 * tenant), then Home (the four-step setup guide, box creation and the shop's sales, as the tenant
 * dashboard's Shopify overview draws them), the boxes, the catalog synced from the shop, the
 * purchases, the ship orders and the opener's appearance, under the Loot Boxes plan's banner when
 * there is something to approve (on Home, the guide asks for a first approval instead).
 */
export function App() {
  const trpc = useTRPC();
  const status = useQuery(trpc.status.queryOptions());
  const placements = usePlacements();
  const screen = useScreen();
  // Home's box builder, opened from the title bar's "Create a box"; leaving Home closes it.
  const [creatingBox, setCreatingBox] = useState(false);
  useEffect(() => {
    if (screen.name !== "Home") setCreatingBox(false);
  }, [screen.name]);

  if (status.isLoading) {
    return (
      <s-page inlineSize="large" heading="Loot Boxes">
        <s-paragraph>Loading…</s-paragraph>
      </s-page>
    );
  }
  if (status.error || !status.data) {
    return (
      <s-page inlineSize="large" heading="Loot Boxes">
        <s-banner tone="critical">{status.error?.message ?? "Could not reach Loot OS."}</s-banner>
      </s-page>
    );
  }
  if (status.data.status !== "active") return <SetupScreen status={status.data} />;
  if (status.data.stoppedReason) {
    return (
      <s-page inlineSize="large" heading="Loot Boxes">
        <s-banner tone="critical" heading="Loot Boxes is paused for this store">
          {status.data.stoppedReason === "tenant_archived"
            ? "Loot Labs has paused this store's account. Contact Loot Labs support to restore it."
            : "Loot Labs has turned the Shopify app off for this store's account. Contact Loot Labs support to turn it back on."}
        </s-banner>
      </s-page>
    );
  }
  const { billing } = status.data;
  // Every "Create a box" is disabled until all five setup steps are done: on Home the setup guide
  // says which is left, on Boxes a line beside the button (and the banner, for the plan).
  const createBlocker = createBoxBlocker(billing, placements);
  const canCreate = createBlocker === null;
  const bannerShown = billing && (screen.name !== "Home" || isPlanApproved(billing.status));

  return (
    <>
      <s-app-nav>
        <s-link href="/" rel="home">
          Home
        </s-link>
        {NAV_SCREENS.map(({ name, path }) => (
          <s-link key={path} href={path}>
            {name}
          </s-link>
        ))}
      </s-app-nav>
      <s-page inlineSize="large" heading={screen.name}>
        {/* Title bar actions are slotted, so they are the page's own children. */}
        {screen.name === "Home" && !creatingBox && (
          <HomeActions canCreate={canCreate} onCreateBox={() => setCreatingBox(true)} />
        )}
        {/* The screens lay out flat on the page, never in a top-level `s-section` (which Polaris
            draws as a card), and `s-page` spaces only its section and banner children. */}
        <s-stack gap="large">
          {bannerShown && <BillingBanner billing={billing} />}
          {screen.name === "Home" && billing && (
            <SetupChecklist
              shopDomain={status.data.shopDomain}
              billing={billing}
              placements={placements}
            />
          )}
          {screen.name === "Home" && (
            <HomeScreen
              canCreate={canCreate}
              creating={creatingBox}
              onCreateBox={() => setCreatingBox(true)}
              onCloseBuilder={() => setCreatingBox(false)}
            />
          )}
          {screen.name === "Boxes" && <BoxesScreen createBlocker={createBlocker} />}
          {screen.name === "Products" && <CatalogScreen />}
          {screen.name === "Purchases" && <PurchasesScreen shopDomain={status.data.shopDomain} />}
          {screen.name === "Items to ship" && <ShipOrdersScreen />}
          {screen.name === "Appearance" && <AppearanceScreen />}
        </s-stack>
      </s-page>
    </>
  );
}
